import { ref, computed } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import useTlmCart from '@/composable/useTlmCart'
import useRouteChange from '@/composable/useRouteChange';
import useCategories from '@/composable/useCategories';
import useUtilities from '@/composable/useUtilities';
import { SHOPPING_LIST_ITEM_STATUS } from '~/constants/ShoppingListItemStatus';
import useCustomer from '~/composable/customer/useCustomer';
import useMitID from '~/composable/useMitID';
import useCheckoutInfo from '@/composable/useCheckoutInfo';
import { CUSTOMER_TYPE } from '~/constants/CheckoutFlow';
import { useShoppingListStore } from '~/stores/useShoppingListStore';
import useSavedUrls from '~/composable/useSavedUrls';

export default function useShoppingList (appContext) {
  const hubbleClient = appContext.$hubble;
  if (!hubbleClient) {
    throw new Error('You must provide the hubbleClient to use this composable');
  }
  const loading = ref(false)
  const notificationAvail = ref(false)
  const notificationMessage = ref('')
  const actionResponse = ref({})

  const category = ref(null)

  const { addSavedAddressInformation } = useCustomer(appContext)
  const { getMitIdToken } = useMitID(appContext)
  const { getCustomerType, getCVRNumberInStorage } = useCheckoutInfo(appContext)
  const { saveBackUrl } = useSavedUrls()
  const {
    cartID,
    addCartItem,
    isMobileBroadBandSubscription,
    isPayGSubscription
  } = useTlmCart(appContext);

  const { getSubscriptionName } = useUtilities()
  const { getCategory } = useCategories(hubbleClient)
  const { routeToCheckout } = useRouteChange()
  const shoppingListStore = useShoppingListStore()
  const {
    shoppingList,
    addressUpdated
  } = storeToRefs(shoppingListStore)

  const createShoppingList = async () => {
    notificationAvail.value = false;
    loading.value = true;
    const res = await hubbleClient.shoppingList.createShoppingList();
    shoppingList.value = getShoppingListWithStatus(res.data);
    appContext.$storage.setLocalStorage('shoppingListID', shoppingList?.value?.id);
    loading.value = false;
    notificationAvail.value = true;
    return res;
  }

  const loadShoppingList = async () => {
    const shoppingListID = appContext.$storage.getLocalStorage('shoppingListID')
    loading.value = true;
    const res = await hubbleClient.shoppingList.loadOrCreateShoppingList(shoppingListID)
    loading.value = false;
    actionResponse.value = res.data;
    shoppingList.value = getShoppingListWithStatus(res?.data?.responseObject);
    appContext.$storage.setLocalStorage('shoppingListID', shoppingList.value.id);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }

  const addShoppingListItem = async (shoppingListID, sku, quantity) => {
    loading.value = true;
    const res = await hubbleClient.shoppingList.addItemToShoppingList(shoppingListID, { sku, quantity });
    loading.value = false;
    actionResponse.value = res.data;
    shoppingList.value = getShoppingListWithStatus(res?.data?.responseObject);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return shoppingList.value;
  }

  const removeItemFromShoppingList = async (shoppingListID, sku) => {
    loading.value = true;
    const res = await hubbleClient.shoppingList.removeItemFromShoppingList(shoppingListID, sku);
    loading.value = false;
    actionResponse.value = res.data;
    shoppingList.value = getShoppingListWithStatus(res?.data?.responseObject);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return shoppingList.value;
  }

  const updateQuantityOfItem = async (shoppingListID, sku, quantity) => {
    loading.value = true;
    const res = await hubbleClient.shoppingList.updateQuantityOfItem(shoppingListID, { sku, quantity });
    loading.value = false;
    actionResponse.value = res.data;
    shoppingList.value = getShoppingListWithStatus(res?.data?.responseObject);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return shoppingList.value;
  }

  const deleteShoppingList = async ({ shoppingListID, reset = false, subscriptionId } = {}) => {
    loading.value = true;
    if (!shoppingListID) {
      shoppingListID = shoppingList.value?.id;
    }
    const res = await hubbleClient.shoppingList.deleteShoppingList(shoppingListID);
    loading.value = false;
    if (subscriptionId !== null && subscriptionId !== undefined) {
      saveBackUrl('/shop/familieabonnement/' + subscriptionId)
    }
    deleteShoppingListLocalStorageID()
    notificationAvail.value = true;
    notificationMessage.value = 'Shopping List Deleted Successfully'
    if (reset) { // remove list also so its not shown in ui
      shoppingList.value = {}
      addressUpdated.value = false
    }
    return res;
  }

  const deleteShoppingListLocalStorageID = () => {
    appContext.$storage.removeLocalStorage('shoppingListID');
  }

  const getShoppingListByID = async (shoppingListID) => {
    loading.value = true;
    const res = await hubbleClient.shoppingList.getShoppingListByID(shoppingListID);
    loading.value = false;
    return res;
  }

  const emptyShoppingList = async (shoppingListID) => {
    loading.value = true;
    if (!shoppingListID) {
      shoppingListID = appContext.$storage.getLocalStorage('shoppingListID');
    }

    const res = await hubbleClient.shoppingList.emptyShoppingList(shoppingListID);
    loading.value = false;
    actionResponse.value = res.data;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }

  const updateShoppingListItemStatus = async (shoppingListID, sku, status, index) => {
    loading.value = true;
    if (!shoppingListID) {
      shoppingListID = appContext.$storage.getLocalStorage('shoppingListID');
    }

    const res = await hubbleClient.shoppingList.updateShoppingListItemStatus(shoppingListID, sku, status, index);
    loading.value = false;
    actionResponse.value = res.data;
    shoppingList.value = getShoppingListWithStatus(res?.data?.responseObject);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return shoppingList.value;
  }

  const shoppingListTotal = computed(() => {
    let total = 0
    shoppingList?.value?.shoppingListItem?.forEach((item, index) => {
      let paymentAmount = item.cartProduct.additionalBillingInformation.hasDiscountPeriod
        ? item.cartProduct.additionalBillingInformation.recurringDiscountPayment.chargeValue
        : item.cartProduct.additionalBillingInformation.recurringPayment.chargeValue
      if (index === 0) { // do not show discounted amount for the parent family subscription in total price per month
        paymentAmount = item.cartProduct.additionalBillingInformation.recurringPayment.chargeValue
      }
      total += paymentAmount
    })
    return total
  })

  const skusInShoppingList = computed(() => {
    const skus = []
    shoppingList?.value?.shoppingListItem?.forEach(item => {
      skus.push(item.sku)
    })
    return skus
  })

  const updateShoppingList = async (shoppingListID, sku, quantity) => {
    if (skusInShoppingList?.value?.includes(sku) && quantity > 0) {
      await updateQuantityOfItem(shoppingListID, sku, quantity)
    } else if (skusInShoppingList?.value?.includes(sku) && quantity === 0) {
      await removeItemFromShoppingList(shoppingListID, sku)
    } else {
      await addShoppingListItem(shoppingListID, sku, 1)
    }
  }

  const getCounterVal = (sku) => {
    if (skusInShoppingList.value.includes(sku)) {
      return shoppingList?.value?.shoppingListItem.find(item => item.sku === sku).quantity
    }
    return 0
  }

  const updateShoppingListItemsStatusOnReceipt = async() => {
    await updateShoppingListItemStatus(shoppingList.value?.id, activeShoppingListItem.value?.sku, SHOPPING_LIST_ITEM_STATUS.COMPLETED, activeShoppingListItem.value?.statusIndex)
  }

  const buySubscriptionWithoutFamilyConcept = async(selectedMixServices, channelSplit, subscriptionId) => {
    await addShoppingListItemToCart(selectedMixServices, channelSplit)
    await deleteShoppingList({ subscriptionId })
    routeToCheckout()
  }

  const buyFamilySubscription = async(selectedMixServices, channelSplit, subscriptionId) => {
    if (shoppingList.value.shoppingListItem.length < 2) {
      await buySubscriptionWithoutFamilyConcept(selectedMixServices, channelSplit, subscriptionId)
    } else {
      let params = null
      if (numberOfUnProcessedItems.value !== shoppingList.value.shoppingListItem.length) {
        params = '?type=' + getCustomerType()
      }
      await updateShoppingListItemStatus(shoppingList.value.id, activeShoppingListItem.value.sku, SHOPPING_LIST_ITEM_STATUS.ACTIVE, activeShoppingListItem.value.statusIndex)
      await addShoppingListItemToCart(selectedMixServices, channelSplit)
      routeToCheckout(params)
    }
  }

  // pass n as the index of the shoppingList item which needs to be added to cart
  const addShoppingListItemToCart = async(selectedMixServices, channelSplit) => {
    // add first item to cart and redirect to checkout page
    category.value = await getCategory({ categorySlug: 'familieabonnement' })
    await addCartItem({
      sku: null,
      channelKey: activeShoppingListItem.value.sku,
      mixProducts: activeShoppingListItem.value.cartProduct.additionalBillingInformation.isMixProduct ? selectedMixServices : null,
      categoryId: category.value?.id,
      channelSplit
    })
  }

  const getFamilySubscriptionName = (listItem) => {
    return getSubscriptionName(listItem.cartProduct.additionalBillingInformation,
      isMobileBroadBandSubscription(listItem.cartProduct.productTypeKey) ? 'Internet med' : ',',
      '',
      isMobileBroadBandSubscription(listItem.cartProduct.productTypeKey),
      isPayGSubscription(listItem.cartProduct.additionalBillingInformation.bundleType))
  }

  const getDisplayPerMonthPrice = (subscription) => {
    return subscription.hasDiscountPeriod
      ? subscription.recurringDiscountPayment.chargeValue
      : subscription.recurringPayment.chargeValue
  }

  const getSum = array => array ? array.reduce((accumulator, currentValue) => accumulator + currentValue, 0) : 0;

  const getTotalMonthlyDiscount = () => {
    // do not include the discount of the parent subscription in the total monthly discount displayed
    const discounts = shoppingList?.value?.shoppingListItem?.map((item, index) => {
      if (index !== 0) {
        const sub = item.cartProduct.additionalBillingInformation
        return sub.recurringPayment.chargeValue - sub.recurringDiscountPayment.chargeValue
      }
      return 0
    })
    return getSum(discounts)
  }

  const getClassForStatus = (listItem) => {
    let classlist = SHOPPING_LIST_ITEM_STATUS[listItem.status]
    if (listItem.cartProduct.additionalBillingInformation.hasDiscountPeriod) {
      classlist += ' is-discounted'
    }
    return classlist
  }

  // this list is a simplified version of the shoppingList with quantity
  // It adds each quantity as a separate item which makes it easy to navigate in loops
  // also adds the quantity index and status which needs to be passed to update shopping list items
  const getShoppingListWithStatus = (shoppingListRes) => {
    const listItemsWithStatus = []
    shoppingListRes?.shoppingListItem?.forEach(item => {
      for (let i = 1; i <= item.quantity; i++) {
        const copyItem = Object.assign({}, item)
        copyItem.status = copyItem.shoppingListStatusMap[i]
        copyItem.statusIndex = i
        listItemsWithStatus.push(copyItem)
      }
    })
    return {
      id: shoppingListRes?.id,
      version: shoppingListRes?.version,
      shoppingListItem: listItemsWithStatus
    }
  }

  // this returns the item which is supposed to be active now
  // if none is active or complete yet then it picks the first item
  const activeShoppingListItem = computed(() => {
    if (shoppingList?.value?.shoppingListItem) {
      let activeItem = shoppingList?.value?.shoppingListItem?.find(lisItem => lisItem.status === 'ACTIVE')
      let lastCompletedItemIndex = null
      shoppingList?.value?.shoppingListItem?.forEach((item, index) => {
        if (item.status === 'COMPLETED') {
          lastCompletedItemIndex = index
        }
      })
      if (!activeItem && lastCompletedItemIndex === null) {
        activeItem = shoppingList?.value?.shoppingListItem[0]
      } else if (!activeItem) {
        activeItem = shoppingList?.value?.shoppingListItem[lastCompletedItemIndex + 1]
      }
      return activeItem
    }
  })

  const numberOfUnProcessedItems = computed(() => {
    let unprocessedItems = 0
    shoppingList?.value?.shoppingListItem?.forEach(item => {
      if (item.status !== 'COMPLETED') {
        unprocessedItems += 1;
      }
    })
    return unprocessedItems
  })

  const addAddressToCart = async () => {
    let tempIdentifier = null
    const customerType = getCustomerType()
    if (customerType === CUSTOMER_TYPE.PRIVATE) {
      tempIdentifier = getMitIdToken()
    } else if (customerType === CUSTOMER_TYPE.BUSINESS) {
      tempIdentifier = getCVRNumberInStorage()
    }
    return await addSavedAddressInformation(cartID.value, tempIdentifier)
  }
  const parentChildSubscriptionsInShoppingList = computed(() => {
    let parentSubscription = {}
    const childSubscriptions = []
    shoppingList?.value?.shoppingListItem.forEach((item, index) => {
      if (index === 0) {
        parentSubscription = item?.cartProduct?.additionalBillingInformation?.displayName + ' - sku: ' + item.sku
      } else {
        childSubscriptions.push(item?.cartProduct?.additionalBillingInformation?.displayName + ' - sku: ' + item.sku)
      }
    })
    return { parentSubscription, childSubscriptions }
  })
  return {
    parentChildSubscriptionsInShoppingList,
    addressUpdated,
    getTotalMonthlyDiscount,
    isLoading: computed(() => loading.value),
    addAddressToCart,
    numberOfUnProcessedItems,
    updateShoppingListItemsStatusOnReceipt,
    activeShoppingListItem,
    updateShoppingListItemStatus,
    getClassForStatus,
    buySubscriptionWithoutFamilyConcept,
    getDisplayPerMonthPrice,
    getFamilySubscriptionName,
    buyFamilySubscription,
    getCounterVal,
    updateShoppingList,
    removeItemFromShoppingList,
    updateQuantityOfItem,
    skusInShoppingList,
    shoppingListTotal,
    shoppingList,
    createShoppingList,
    deleteShoppingListCookie: deleteShoppingListLocalStorageID,
    emptyShoppingList,
    getShoppingListByID,
    deleteShoppingList,
    addShoppingListItem,
    loadShoppingList,
    shoppingListExists: computed(() => {
      return appContext.$storage.getLocalStorage('shoppingListID') !== undefined &&
      appContext.$storage.getLocalStorage('shoppingListID') !== null
    })
  }
}
